import { AssetType } from '@/modules/asset/enums/AssetType';
import { ColumnFormat } from '@/modules/core/app/constants/data.constants';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { BaseExtractedRecordModel } from '@/modules/ai/models/abstracts/BaseExtractedRecordModel';
import Lang from '@/modules/core/app/services/Lang';
import NumeralService from '@/modules/core/app/services/NumeralService';

export class ExtractedAsset extends BaseExtractedRecordModel {
  id?: number;
  documentExtractionId?: number;
  assetId?: number;
  name?: string;
  type?: string;
  estimatedValue?: number;
  currentValue?: number;
  atDeathValue?: number;
  purchasePrice?: number;
  currencyId?: number;
  shareOwnershipType?: string;
  isValidated?: boolean;

  constructor(model: Partial<ExtractedAsset> = {}) {
    super(model);

    this.id = model.id;
    this.documentExtractionId = model.documentExtractionId;
    this.assetId = model.assetId;
    this.name = model.name;
    this.type = model.type;
    this.estimatedValue = model.estimatedValue;
    this.currentValue = model.currentValue;
    this.atDeathValue = model.atDeathValue;
    this.purchasePrice = model.purchasePrice;
    this.currencyId = model.currencyId;
    this.shareOwnershipType = model.shareOwnershipType;
    this.isValidated = model.isValidated;
  }

  getModelName(): ModelType {
    return ModelType.EXTRACTED_ASSET;
  }

  get hasMatch(): boolean {
    return !!this.assetId;
  }

  get showPriceColumn(): boolean {
    return [
      AssetType.REAL_ESTATE.key,
      AssetType.LIFE_INSURANCE.key,
      AssetType.ART_JEWELRY.key,
      AssetType.BELONGING.key,
      AssetType.PRIVATE_STOCK.key,
      AssetType.OTHER.key
    ].includes(this.type as string);
  }

  get priceAmount(): number | undefined {
    if (this.type === AssetType.LIFE_INSURANCE.key) {
      return this.atDeathValue;
    }

    return this.purchasePrice;
  }

  get formattedPriceAmount(): string {
    return NumeralService.formatValue(this.priceAmount, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  get formattedPriceAmountSubLabel(): string {
    if (this.type === AssetType.LIFE_INSURANCE.key) {
      return Lang.t('referral.prefilling-inventory-benefit-amount');
    }

    return Lang.t('referral.prefilling-inventory-purchase-price');
  }

  get mainAmount(): number | undefined {
    if (this.type === AssetType.LIFE_INSURANCE.key) {
      return this.currentValue ?? this.estimatedValue;
    }

    return this.atDeathValue ?? this.currentValue ?? this.estimatedValue;
  }

  get formattedAmount(): string {
    return NumeralService.formatValue(this.mainAmount, ColumnFormat.FORMAT_CURRENCY, 2);
  }

  get formattedAmountSubLabel(): string {
    if (this.type === AssetType.LIFE_INSURANCE.key || this.type === AssetType.CASH.key) {
      return Lang.t('referral.prefilling-inventory-cash-value');
    }

    if (this.type === AssetType.PRIVATE_STOCK.key) {
      return Lang.t('referral.prefilling-inventory-total-value');
    }

    return Lang.t('referral.prefilling-inventory-market-value');
  }
}
